"use client"

import { useEffect, useRef, useState, type FormEvent, type ReactElement } from "react"
import { submitBonusRequest } from "@lib/data/bonus"
import CheckoutButton from "@modules/checkout/components/checkout-button"
import {
  emptyBonusFormValues,
  firstInvalidFieldName,
  toBonusSubmission,
  validateBonusField,
  validateBonusForm,
  type BonusFormErrors,
  type BonusFormField,
  type BonusFormValues,
} from "@modules/bonus/lib/bonus-form-model"
// Story 10.6 — o renderizador de campo saiu daqui para `modules/common/components/form-field`: era a
// 2.ª ocorrência do padrão (o próprio comentário dele dizia "duplica-se agora e extrai-se depois") e a
// candidatura a afiliado seria a 3.ª. O componente é o MESMO (vidro, 48px, foco, erro por
// `aria-describedby`); só o `data-testid` passou a vir de um prop, para os testids do Bónus
// (`bonus-field-*`) não mudarem por causa de uma extração.
import FormField, { type FormFieldElement } from "@modules/common/components/form-field"

// Story 8.1 — o formulário de Bónus. Renderiza-se A PARTIR DA DEFINIÇÃO servida por
// `GET /store/bonus-form` (AC3): não há um único campo escrito à mão neste JSX. Toda a regra vive no
// módulo puro `lib/bonus-form-model` (que é o que a suite testa — o harness do storefront não tem DOM).
//
// Segue o padrão de form MODERNO da casa (`checkout/components/fiscal-billing`): `useState` para
// valores + erros + erro de formulário + `submitting`, `validate()` à mão, erro que limpa ao editar,
// `await serverAction()`. NÃO o `useActionState` + shim de `@medusajs/ui` do `modules/account/*`, que é
// resíduo do starter em tokens de tema claro.
//
// DIVERGE do `fiscal-billing` num ponto, de propósito: aqui existe um `<form onSubmit>` de verdade. O
// do checkout é um botão `type="button"` solto, logo Enter não submete — num passo de checkout isso
// esconde-se, numa página pública de formulário único seria uma falha de expectativa básica.

const SUBMIT_LABEL = "Enviar solicitação"

// A rede falhou ANTES de o server action correr (ver o `catch` do `handleSubmit`). Mesmo texto do
// `SUBMIT_ERROR_MESSAGE` de `lib/data/bonus`, duplicado por ser outro pacote: aquele é `"use server"` e
// importar dele arrastaria o módulo de servidor para o bundle do cliente.
const TRANSPORT_ERROR_MESSAGE =
  "Não foi possível registrar sua solicitação agora. Tente novamente em instantes."

const BonusForm = ({ fields }: { fields: BonusFormField[] }): ReactElement => {
  const [values, setValues] = useState<BonusFormValues>(() => emptyBonusFormValues(fields))
  const [errors, setErrors] = useState<BonusFormErrors>({})
  const [formError, setFormError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  // Um ref por campo — é o que permite ANCORAR o foco no 1.º inválido depois de um submit falhado.
  // Sem `forwardRef` no `FormField` isto seria impossível (a razão de não reusar o `CheckoutField`).
  const fieldRefs = useRef<Record<string, FormFieldElement | null>>({})

  // Campos que o utilizador JÁ editou. O `blur` só acusa erro nestes: sem isto, quem passa o formulário
  // à tecla TAB para o ler acende a vermelho todos os obrigatórios sem ter escrito uma letra — um erro
  // que mente sobre o que a pessoa fez. É a contraparte da regra do `handleChange` (nunca um vermelho
  // que mente). No SUBMIT valida-se tudo, tocado ou não: aí a intenção de enviar é explícita.
  const touchedRef = useRef<Record<string, boolean>>({})

  // Guarda SÍNCRONA de duplo envio. Tem de ser um ref e não o estado `submitting`: dois submits
  // despachados no mesmo tick (Enter + clique, duplo-clique) leem ambos o MESMO `submitting === false`
  // da render em que foram criados, logo passariam os dois — a guarda por estado não fecha a corrida
  // que existe para fechar. O ref muda no instante em que se lê, antes do primeiro `await`.
  const submittingRef = useRef(false)

  // O heading da confirmação — para onde o foco salta quando o formulário se desmonta (ver o ramo
  // `submitted`). É a mesma disciplina da âncora do 1.º campo inválido: o foco nunca fica órfão.
  const confirmationRef = useRef<HTMLHeadingElement | null>(null)

  useEffect(() => {
    if (submitted) {
      confirmationRef.current?.focus()
    }
  }, [submitted])

  const handleChange = (field: BonusFormField) => (raw: string): void => {
    // O valor entra COMO FOI ESCRITO — nenhuma reformatação. Ver a nota sobre a máscara de telefone em
    // `lib/bonus-form-model`: era ela que corrompia o número de um prospect estrangeiro.
    touchedRef.current[field.name] = true
    setValues((previous) => ({ ...previous, [field.name]: raw }))
    // O erro de FORMULÁRIO também limpa ao editar, e não só o do campo: as mensagens de 400 do Engine são
    // sobre UM campo ("E-mail inválido.", "Informe Nome completo.") mas aterram aqui, no canal de
    // formulário, porque a resposta não diz QUAL campo. Sem isto, corrigir o e-mail deixava a recusa do
    // servidor acesa por baixo do botão até ao submit seguinte — exatamente o "vermelho a mentir" que a
    // regra abaixo existe para impedir, só que pelo outro canal.
    setFormError(null)
    // O erro limpa ao EDITAR: nunca "campo já corrigido + vermelho a mentir". Revalida no blur/submit.
    setErrors((previous) => {
      if (previous[field.name] === undefined) {
        return previous
      }
      const next = { ...previous }
      delete next[field.name]
      return next
    })
  }

  // Validação no BLUR: o erro aparece ao sair do campo, não só depois de tentar submeter — mas SÓ se o
  // campo já foi editado (ver `touchedRef`). Passar por um campo sem lhe tocar não é um erro do
  // utilizador, é leitura.
  const handleBlur = (field: BonusFormField) => (): void => {
    if (!touchedRef.current[field.name]) {
      return
    }
    const error = validateBonusField(field, values[field.name] ?? "")
    setErrors((previous) => {
      const next = { ...previous }
      if (error === null) {
        delete next[field.name]
      } else {
        next[field.name] = error
      }
      return next
    })
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    // O `<form>` existe para o Enter submeter; o POST nativo do browser nunca deve acontecer.
    event.preventDefault()

    // GUARDA DE DUPLO ENVIO, e o `loading` do botão NÃO a torna redundante: `setSubmitting(true)` é
    // assíncrono, logo dois submits despachados no mesmo tick (Enter + clique, duplo-clique) passam
    // os DOIS antes de o botão sequer re-renderizar desabilitado. Não há idempotência do lado do
    // Engine — cada passagem grava a sua linha `bonus_request`, e o admin da 8.2 via o mesmo prospect
    // duas vezes. Por isso a guarda lê o REF (síncrono) e não o estado: ver `submittingRef`.
    if (submittingRef.current) {
      return
    }
    setFormError(null)

    const validationErrors = validateBonusForm(fields, values)
    setErrors(validationErrors)
    const firstInvalid = firstInvalidFieldName(fields, validationErrors)
    if (firstInvalid !== null) {
      // Ancorar o foco (e não só pintar de vermelho): num form de 8 campos, o inválido pode estar fora
      // do ecrã — `focus()` leva-o à vista e anuncia-o ao leitor de ecrã.
      fieldRefs.current[firstInvalid]?.focus()
      return
    }

    // Fecha a porta ANTES do primeiro `await`: é isto que faz o 2.º submit do mesmo tick voltar atrás.
    submittingRef.current = true
    setSubmitting(true)
    try {
      // O CORPO do server action nunca lança: devolve `{ok:false, error}` (incl. a mensagem PT-BR do
      // validador autoritário do Engine num 400). Mas o `await` atravessa uma FRONTEIRA RPC, e essa
      // rejeita por conta própria — offline, 5xx do Next, deployment id obsoleto. Esse `catch` corre no
      // servidor e nunca chega cá; só o `catch` abaixo apanha a falha de transporte.
      const result = await submitBonusRequest(toBonusSubmission(fields, values))

      if (!result.ok) {
        setFormError(result.error)
        return
      }
      setSubmitted(true)
    } catch (error) {
      // Sem isto, submeter sem rede não fazia NADA visível: a rejeição subia como unhandled, o `finally`
      // repunha o botão no estado inicial e o utilizador ficava sem erro e sem confirmação — a olhar para
      // um formulário que parecia não ter reagido. A audiência são prospects em telemóvel, onde perder a
      // rede a meio é o caso comum, não o exótico. Erro nunca engolido: loga a causa e diz-lho.
      console.error("[bonus] falha de transporte ao submeter a solicitação:", error)
      setFormError(TRANSPORT_ERROR_MESSAGE)
    } finally {
      // `finally` e não duas chamadas: qualquer que seja a saída, o formulário não pode ficar preso a
      // "Processando..." para sempre, mudo e sem botão.
      submittingRef.current = false
      setSubmitting(false)
    }
  }

  // Confirmação pós-submissão: o utilizador NUNCA fica sem estado. Diz o que ficou registado E o que
  // acontece a seguir (análise manual) — prometer prazo seria inventar o que o fluxo não garante.
  if (submitted) {
    return (
      <div
        role="status"
        className="rounded-large border border-white/10 bg-white/[0.03] p-6 backdrop-blur-xl"
        data-testid="bonus-form-success"
      >
        {/*
          O foco ANCORA aqui, pela mesma razão que ancora no 1.º campo inválido: este ramo desmonta o
          formulário INTEIRO, incluindo o botão que tinha o foco. Sem o mover, o foco cai no `<body>` —
          quem navega por teclado perde o lugar e volta ao topo da página, e um `role="status"` acabado
          de montar anuncia de forma inconsistente (o leitor de ecrã não vigiava um nó que não existia).
          `tabIndex={-1}` torna o heading focável por script sem o pôr na ordem de tabulação.
        */}
        <h2 ref={confirmationRef} tabIndex={-1} className="text-lg font-extrabold text-text focus:outline-none focus:ring-2 focus:ring-accent">
          Solicitação registrada
        </h2>
        <p className="mt-2 text-sm text-subtle">
          Recebemos sua solicitação e ela está <strong className="text-text">aguardando análise</strong>
          . Nossa equipe entra em contato pelo e-mail informado.
        </p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5" data-testid="bonus-form">
      {/* Coluna única em ≤768px; duas colunas a partir de `md`. A `textarea` ocupa a linha inteira. */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {fields.map((field) => (
          <div key={field.name} className={field.type === "textarea" ? "md:col-span-2" : undefined}>
            <FormField
              ref={(element) => {
                fieldRefs.current[field.name] = element
              }}
              field={field}
              value={values[field.name] ?? ""}
              onChange={handleChange(field)}
              onBlur={handleBlur(field)}
              error={errors[field.name]}
              // O prefixo dos testids do Bónus, INTACTO desde a 8.1: a extração do componente não pode
              // renomear a superfície que o QA endereça.
              testIdPrefix="bonus-field"
            />
          </div>
        ))}
      </div>

      {formError && (
        <p role="alert" className="text-sm text-danger-bright" data-testid="bonus-form-error">
          {formError}
        </p>
      )}

      <div>
        {/* `loading` desabilita o botão e mostra "Processando..." — sem duplo envio, sem estado mudo. */}
        <CheckoutButton type="submit" loading={submitting} data-testid="bonus-submit-button">
          {SUBMIT_LABEL}
        </CheckoutButton>
      </div>
    </form>
  )
}

export default BonusForm
